import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Search, CreditCard, Crown, Sparkles, CheckCircle2, XCircle, AlertTriangle, Users, Receipt, Landmark } from "lucide-react";
import { PageLoader, LoadError, EmptyState } from "@/components/admin/LoadState";
import { usePageTitle } from "@/hooks/usePageTitle";
import { fmtDate, fmtMoney, fmtNum, norm } from "@/lib/format";
import { SubscriptionLedger, INTERVAL, CHANNEL } from "@/components/admin/SubscriptionLedger";
import { DebitPanel } from "@/components/admin/DebitPanel";

// Estados de Stripe traducidos (antes se mostraban en inglés: past_due, canceled…).
const STATUS: Record<string, { label: string; tone: "ok" | "warn" | "off" }> = {
  active: { label: "Activa", tone: "ok" },
  trialing: { label: "En prueba", tone: "ok" },
  past_due: { label: "Pago atrasado", tone: "warn" },
  unpaid: { label: "Impaga", tone: "warn" },
  incomplete: { label: "Pago incompleto", tone: "warn" },
  incomplete_expired: { label: "Pago no completado", tone: "off" },
  canceled: { label: "Cancelada", tone: "off" },
  paused: { label: "Pausada", tone: "off" },
};

export default function AdminSubscriptions() {
  usePageTitle("Suscripciones");
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const view = ["cobros", "debito"].includes(params.get("vista") || "") ? params.get("vista") : "suscriptores";
  const setView = (v: string) => setParams(v === "suscriptores" ? {} : { vista: v }, { replace: true });

  const load = () => {
    setFailed(false);
    return api.get("/admin/subscriptions").then((r) => setData(r.data)).catch(() => setFailed(true)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const subs = data?.subscriptions || [];
  const counts = data?.counts || { total: 0, active: 0, fitrewards: 0, fitrewards_plus: 0 };
  const filtered = subs.filter((u: any) => !q || [u.name, u.cedula, u.email].some((v) => norm(v).includes(norm(q))));

  if (loading) return <PageLoader />;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-subscriptions">
      <div>
        <p className="text-sm text-slate-600">Planes Armony Contigo</p>
        <h1 className="text-3xl font-serif font-medium">Suscripciones</h1>
        <p className="text-sm text-slate-600 mt-1">Clientes que contrataron Armony Contigo Plus o Armony Contigo Premium, el estado de su suscripción y lo cobrado por cada periodo.</p>
      </div>

      <div role="tablist" aria-label="Vistas de suscripciones" className="inline-flex p-1 rounded-full bg-slate-100">
        {[{ key: "suscriptores", label: "Suscriptores", icon: Users }, { key: "cobros", label: "Cobros y licencias", icon: Receipt }, { key: "debito", label: "Débito Armony", icon: Landmark }].map((t) => (
          <button key={t.key} type="button" role="tab" aria-selected={view === t.key} onClick={() => setView(t.key)} data-testid={`subscriptions-tab-${t.key}`}
            className={`px-4 py-2 rounded-full text-sm font-medium flex items-center gap-1.5 ${view === t.key ? "bg-navy text-white" : "text-slate-700 hover:text-slate-900"}`}>
            <t.icon className="w-4 h-4" aria-hidden="true" /> {t.label}
          </button>
        ))}
      </div>

      {view === "cobros" ? (
        <SubscriptionLedger />
      ) : view === "debito" ? (
        <DebitPanel />
      ) : failed ? (
        <LoadError what="las suscripciones" onRetry={load} />
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <CountCard icon={CreditCard} label="Total de suscriptores" value={counts.total} />
            <CountCard icon={CheckCircle2} label="Con suscripción activa" value={counts.active} accent />
            <CountCard icon={Sparkles} label="Armony Contigo Plus" value={counts.fitrewards} />
            <CountCard icon={Crown} label="Armony Contigo Premium" value={counts.fitrewards_plus} gold />
          </div>

          <div className="relative max-w-md">
            <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" aria-hidden="true" />
            <Input
              data-testid="subscriptions-search"
              aria-label="Buscar suscripciones"
              placeholder="Buscar por nombre, cédula o correo"
              value={q}
              onChange={(e: any) => setParams(e.target.value ? { q: e.target.value } : {}, { replace: true })}
              className="pl-9 h-11"
            />
          </div>

          {filtered.length === 0 ? (
            <EmptyState icon={CreditCard}>
              <span data-testid="subscriptions-empty">
                {q ? <>No hay suscripciones que coincidan con “{q}”.</> : "Aún no hay clientes con planes contratados."}
              </span>
            </EmptyState>
          ) : (
            <div className="card-obsidian overflow-hidden">
              <div className="overflow-x-auto" tabIndex={0} role="region" aria-label="Tabla de suscripciones">
                <table className="w-full text-sm min-w-[860px]">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wider text-slate-600 border-b border-slate-200">
                      <th scope="col" className="px-4 py-3 font-medium">Cliente</th>
                      <th scope="col" className="px-4 py-3 font-medium">Contacto</th>
                      <th scope="col" className="px-4 py-3 font-medium">Plan</th>
                      <th scope="col" className="px-4 py-3 font-medium">Contratado</th>
                      <th scope="col" className="px-4 py-3 font-medium">Precio</th>
                      <th scope="col" className="px-4 py-3 font-medium">Estado</th>
                      <th scope="col" className="px-4 py-3 font-medium">Renueva o vence</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((u: any) => {
                      const st = STATUS[u.subscription_status] || { label: u.subscription_status ? "Sin dato claro" : "—", tone: "off" };
                      return (
                        <tr key={u.id} data-testid={`subscription-row-${u.id}`} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                          <td className="px-4 py-3">
                            <Link to={`/admin/usuarios/${u.id}`} className="font-medium text-slate-900 hover:underline">{u.name}</Link>
                            <p className="text-xs text-slate-600">Cédula {u.cedula || "—"}</p>
                          </td>
                          <td className="px-4 py-3">
                            <p className="text-slate-700">{u.email}</p>
                            <p className="text-xs text-slate-600">{u.phone || "Sin teléfono"}</p>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${u.plan === "fitrewards_plus" ? "bg-gold/15 text-gold-ink" : "bg-navy/10 text-navy"}`}>
                              {u.plan === "fitrewards_plus" ? <Crown className="w-3 h-3" aria-hidden="true" /> : <Sparkles className="w-3 h-3" aria-hidden="true" />}
                              {u.plan_name || "Plan sin nombre"}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-slate-700 whitespace-nowrap">{fmtDate(u.started_at)}</td>
                          <td className="px-4 py-3 text-slate-700 whitespace-nowrap">
                            {u.price != null ? fmtMoney(u.price, u.currency) : "—"}
                            <p className="text-xs text-slate-600">{INTERVAL[u.plan_interval] || "—"} · {CHANNEL[u.billing_channel] || "—"}</p>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center gap-1 text-xs font-medium ${st.tone === "ok" ? "text-emerald-800" : st.tone === "warn" ? "text-amber-800" : "text-slate-600"}`}>
                              {st.tone === "ok" ? <CheckCircle2 className="w-3.5 h-3.5" aria-hidden="true" /> : st.tone === "warn" ? <AlertTriangle className="w-3.5 h-3.5" aria-hidden="true" /> : <XCircle className="w-3.5 h-3.5" aria-hidden="true" />}
                              {st.label}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-slate-700">
                            {fmtDate(u.cancel_at || u.current_period_end)}
                            {u.next_plan_name && !u.cancel_at && (
                              <p className="text-xs text-navy font-medium">Pasa a {u.next_plan_name} · {INTERVAL[u.next_interval] || "—"}</p>
                            )}
                            {u.cancel_at && u.subscription_status === "active" && (
                              <p className="text-xs text-amber-800 font-medium" data-testid="subscription-cancel-scheduled">Cancelada: termina ese día, no renueva</p>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const CountCard = ({ icon: Icon, label, value, accent, gold }: any) => (
  <div className="card-obsidian p-5">
    <div className="flex items-center justify-between gap-2">
      <p className="text-sm text-slate-600">{label}</p>
      <Icon className={`w-4 h-4 shrink-0 ${gold ? "text-gold-ink" : accent ? "text-emerald-700" : "text-navy"}`} aria-hidden="true" />
    </div>
    <p className="text-3xl font-serif font-medium text-slate-900 mt-2">{fmtNum(value)}</p>
  </div>
);
